import React, { useState } from "react";
import { Box, Menu, MenuItem, Tooltip } from "@mui/material";
import LockOutlined from "@mui/icons-material/LockOutlined";
import { ConfirmModal } from "global/components/AppModal";
import strings from "global/constants/StringConstants";
import { useFeatures } from "hooks/use-feature";
import { hasAccessTo } from "layouts/Config";
import { menu } from "theme/colors";

/** One row of the request kebab menu. */
export interface RequestMenuItem {
  label: string;
  onClick: Function;
  /** Renders the row in the destructive style ("Void request", "Delete"). */
  danger?: boolean;
  /** Feature key gating the row — disabled with a tooltip when unavailable. */
  featureKey?: string;
  /** Asks for confirmation in a dialog before running `onClick`. */
  confirm?: {
    title: string;
    body: string;
    confirmText: string;
  };
}

interface RequestActionsMenuProps {
  popover: {
    open: boolean;
    anchorEl: HTMLElement | null;
    setAnchorEl: (value: React.SetStateAction<HTMLElement | null>) => void;
    onOpen: (event: React.MouseEvent<HTMLButtonElement>) => void;
    onClose: () => void;
  };
  items: RequestMenuItem[];
}

/** Tooltip copy for a row the current plan / admin has switched off. */
const featureTooltip = (status: string, disabledMessage?: string) => {
  if (status === strings.FEATURE_STATUS_ADMIN_DISABLED) {
    return (
      disabledMessage ?? strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP
    );
  }
  if (status === strings.FEATURE_STATUS_NOT_IN_PLAN) {
    return strings.FEATURE_GATE_UPSELL_TOOLTIP;
  }
  return "";
};

/**
 * Kebab menu shown on every sign-request row (sent list, folder view). The card
 * chrome, item rhythm and destructive colour come from the global `MuiMenu` /
 * `MuiMenuItem` theme overrides — this component only supplies the rows, the
 * feature gating and the confirmation dialogs the destructive actions need.
 */
const RequestActionsMenu: React.FC<RequestActionsMenuProps> = ({
  popover,
  items,
}) => {
  const { state } = useFeatures();
  const [itemToConfirm, setItemToConfirm] = useState<RequestMenuItem | null>(
    null,
  );

  const isSignAccess = hasAccessTo(
    strings.resources.sign,
    strings.sendPermission,
  );
  const filteredMenuItems = items.filter(
    (item) => !(item.label === strings.update && !isSignAccess),
  );

  const handleSelect = (item: RequestMenuItem) => {
    popover.onClose();
    if (item.confirm) {
      setItemToConfirm(item);
      return;
    }
    item.onClick();
  };

  const handleConfirm = () => {
    const item = itemToConfirm;
    setItemToConfirm(null);
    item?.onClick();
  };

  const renderItem = (item: RequestMenuItem) => {
    const featureState = item.featureKey
      ? state(item.featureKey)
      : { status: strings.FEATURE_STATUS_AVAILABLE };
    const isAvailable =
      featureState.status === strings.FEATURE_STATUS_AVAILABLE;

    const menuItem = (
      <MenuItem
        key={item.label}
        disabled={!isAvailable}
        data-danger={item.danger ? "true" : undefined}
        onClick={() => handleSelect(item)}
      >
        {item.label}
        {!isAvailable && (
          <LockOutlined
            sx={{ fontSize: 14, color: "text.disabled", ml: "auto" }}
          />
        )}
      </MenuItem>
    );

    if (isAvailable) return menuItem;

    return (
      <Tooltip
        key={item.label}
        title={featureTooltip(
          featureState.status,
          (featureState as any).disabledMessage,
        )}
        placement="left"
        arrow
      >
        <Box component="span" sx={{ display: "block" }}>
          {menuItem}
        </Box>
      </Tooltip>
    );
  };

  return (
    <>
      <Menu
        anchorEl={popover.anchorEl}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        open={popover.open}
        onClose={popover.onClose}
        PaperProps={{ sx: { minWidth: menu.minWidth } }}
        MenuListProps={{ "aria-label": "Request actions" }}
      >
        {filteredMenuItems.map(renderItem)}
      </Menu>

      <ConfirmModal
        open={Boolean(itemToConfirm)}
        onClose={() => setItemToConfirm(null)}
        title={itemToConfirm?.confirm?.title ?? ""}
        message={itemToConfirm?.confirm?.body}
        // The rows flagged `danger` ("Void request", "Delete") confirm in the
        // destructive tone; everything else is a plain confirm.
        tone={itemToConfirm?.danger ? "destructive" : "neutral"}
        confirmLabel={itemToConfirm?.confirm?.confirmText ?? ""}
        onConfirm={handleConfirm}
      />
    </>
  );
};

export default RequestActionsMenu;
